/**
 * AP-3 — Portauswahl und -auflösung.
 * Ersetzt `CanableAdapter::ScanCandidates()` (SetupAPI-Enumeration + ScorePort):
 * der Browser kennt keine Enumeration ohne Nutzergeste, siehe CONTRACTS.md §2.2.
 */

import { CanAdapterError, KNOWN_USB_FILTERS, type PortRef } from './ICanAdapter';

export function hasWebSerial(): boolean {
  return typeof navigator !== 'undefined' && 'serial' in navigator;
}

function hex4(value: number | undefined): string {
  return value === undefined ? '????' : value.toString(16).toUpperCase().padStart(4, '0');
}

/** Anzeigetext, z. B. "CANable (AD50:60C4)" — nicht identitätsbildend. */
export function describePort(info: SerialPortInfo, index: number): string {
  if (info.usbVendorId === undefined) return `Serieller Port #${index + 1}`;
  const known = KNOWN_USB_FILTERS.some(
    (filter) => filter.usbVendorId === info.usbVendorId && filter.usbProductId === info.usbProductId,
  );
  const ids = `${hex4(info.usbVendorId)}:${hex4(info.usbProductId)}`;
  return known ? `CANable (${ids})` : `USB-Seriell (${ids})`;
}

/**
 * Fordert die Freigabe eines Ports an. **Muss** im Main-Thread aus einer
 * Nutzergeste heraus laufen. `allowAll = true` entspricht dem Fallback
 * "alle Ports anzeigen", wenn kein bekanntes CANable-Gerät gefunden wird.
 */
export async function requestPort(allowAll = false): Promise<PortRef> {
  if (!hasWebSerial()) {
    throw new CanAdapterError('no-web-serial', 'Web Serial wird von diesem Browser nicht unterstützt');
  }
  let port: SerialPort;
  try {
    port = await navigator.serial.requestPort(
      allowAll ? {} : { filters: KNOWN_USB_FILTERS.map((filter) => ({ ...filter })) },
    );
  } catch (cause) {
    throw new CanAdapterError('no-port', 'Es wurde kein Port ausgewählt', { cause });
  }

  const ports = await navigator.serial.getPorts();
  const index = ports.indexOf(port);
  const info = port.getInfo();
  const ref: PortRef = { index: index < 0 ? 0 : index, label: describePort(info, index < 0 ? 0 : index) };
  if (info.usbVendorId !== undefined) ref.usbVendorId = info.usbVendorId;
  if (info.usbProductId !== undefined) ref.usbProductId = info.usbProductId;
  return ref;
}

/** Alle bereits freigegebenen Ports — ersetzt die Kandidatenliste des Originals. */
export async function listPorts(): Promise<PortRef[]> {
  if (!hasWebSerial()) return [];
  const ports = await navigator.serial.getPorts();
  return ports.map((port, index) => {
    const info = port.getInfo();
    const ref: PortRef = { index, label: describePort(info, index) };
    if (info.usbVendorId !== undefined) ref.usbVendorId = info.usbVendorId;
    if (info.usbProductId !== undefined) ref.usbProductId = info.usbProductId;
    return ref;
  });
}

/**
 * Löst einen `PortRef` auf: erst über `index`, und nur wenn dort die Vid/Pid
 * passt. Sonst erster passender Eintrag; ohne Treffer `no-port`
 * (CONTRACTS.md §2.2).
 */
export async function resolvePort(ref: PortRef): Promise<SerialPort> {
  if (!hasWebSerial()) {
    throw new CanAdapterError('no-web-serial', 'Web Serial wird von diesem Browser nicht unterstützt');
  }
  const ports = await navigator.serial.getPorts();
  if (ports.length === 0) {
    throw new CanAdapterError('no-port', 'Kein freigegebener serieller Port vorhanden');
  }

  const matches = (port: SerialPort): boolean => {
    if (ref.usbVendorId === undefined) return true;
    const info = port.getInfo();
    return info.usbVendorId === ref.usbVendorId && info.usbProductId === ref.usbProductId;
  };

  const atIndex = ports[ref.index];
  if (atIndex !== undefined && matches(atIndex)) return atIndex;

  const fallback = ports.find(matches);
  if (fallback !== undefined) return fallback;

  throw new CanAdapterError('no-port', `Port "${ref.label}" ist nicht mehr freigegeben`);
}
